import { useEffect, useState } from "react";
import { useSession, useUser } from "@clerk/react";
import { Button, Card, Tabs, toast } from "@heroui/react";
import { Download, Share2, Star, Pencil, Trash2, RefreshCw, X } from "lucide-react";
import MeetingAudioPlayer from "@/components/audio-player";
import { getByDocId } from "@/api/meets";
import { getAudioDownloadUrl, processAudio } from "@/api/audio";
import { getSummaryByMeetsId } from "@/api/meets-summary";
import { getDetailedByMeetsId } from "@/api/meets-detailed";
import { doPrivatePostRequest } from "@/api/requests";
import { resolveRole } from "@/utils/roles";

export default function RecordingDetailsComp({ _recording_id, record: initialRecord, onClose, onDeleted, refreshKey = 0 }) {
  const { session } = useSession();
  const { user } = useUser();
  const recordId = _recording_id || initialRecord?._id;
  const [record, setRecord] = useState(null);
  const [summary, setSummary] = useState([]);
  const [transcript, setTranscript] = useState([]);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [refresh, setRefresh] = useState(0);
  const [tab, setTab] = useState("summary");
  const [deleted, setDeleted] = useState(false);

  useEffect(() => {
    if (!session || !recordId) return;
    let cancelled = false;
    let timer;
    setRecord(null); setSummary([]); setTranscript([]); setError(""); setDeleted(false);
    const load = async () => {
      try {
        const token = await session.getToken();
        const responses = await Promise.all([
          getByDocId(recordId, token), getSummaryByMeetsId(recordId, token), getDetailedByMeetsId(recordId, token),
        ]);
        if (responses.some(r => r.data.Result !== "OK")) throw new Error("Could not load this recording.");
        if (cancelled) return;
        const next = responses[0].data.Record;
        setRecord(next); setSummary(responses[1].data.Records || []); setTranscript(responses[2].data.Records || []);
        setError("");
        const status = next.processing?.complete_recording?.status;
        if (status === "queued" || status === "running") timer = setTimeout(load, 5000);
      } catch (err) {
        if (!cancelled) setError(err.response?.data?.detail || err.message || "Recording unavailable.");
      }
    };
    load();
    return () => { cancelled = true; clearTimeout(timer); };
  }, [recordId, session, refresh, refreshKey]);

  if (deleted) return <Card><Card.Content>Recording deleted. The meeting remains available.</Card.Content></Card>;
  if (!recordId) return <p className="p-6 text-muted">Select a recording.</p>;
  if (error && !record) return <Card><Card.Content><p>{error}</p><Button onPress={() => setRefresh(n => n + 1)}>Retry</Button></Card.Content></Card>;
  if (!record) return <p className="p-6 text-muted">Loading recording…</p>;

  const canEdit = record.created_by?.user_id === user?.id || resolveRole(user) === "admin";
  const status = record.processing?.complete_recording?.status;
  const processing = status === "queued" || status === "running";
  const act = async (fn, message) => {
    setBusy(true);
    try { await fn(); if (message) toast.success(message); }
    catch (err) { toast.danger(err.response?.data?.detail || err.message || "Action failed"); }
    finally { setBusy(false); }
  };
  const update = async (patch) => {
    const { data } = await doPrivatePostRequest({ meeting_id: recordId, ...patch }, "/audio/api/recording/update", await session.getToken());
    if (data.Result !== "SUCCESS") throw new Error(data.Message || "Update failed");
    setRecord(data.Record);
  };
  const copyLink = () => act(async () => {
    // This link preserves authentication; signed media URLs are never shared.
    const url = `${window.location.origin}/recordings?selected=${encodeURIComponent(recordId)}`;
    await navigator.clipboard.writeText(url);
  }, "Link copied. Only meeting participants and admins can open it.");
  const remove = () => {
    if (!window.confirm("Delete this recording, its audio fragments, summary and transcript? The meeting will remain.")) return;
    act(async () => {
      const { data } = await doPrivatePostRequest({ meeting_id: recordId }, "/audio/api/recording/delete", await session.getToken());
      if (data.Result !== "SUCCESS") throw new Error(data.Message);
      setDeleted(true); onDeleted?.();
    }, "Recording deleted");
  };
  const retry = () => act(async () => {
    await processAudio(recordId, await session.getToken());
    setRefresh(n => n + 1);
  }, "Processing queued");
  const emptyText = status === "failed" ? "Processing failed. The host can retry." : processing ? "Processing recording…" : "No generated text is available.";

  return <Card>
    <Card.Header className="flex-row justify-between">
      <div><h2 className="text-lg font-bold">{record.title}</h2><p className="text-sm text-muted">{record.meeting_date} · {record.start_time?.set}</p></div>
      {onClose && <Button isIconOnly variant="ghost" onPress={onClose} aria-label="Close"><X /></Button>}
    </Card.Header>
    <Card.Content className="flex-col gap-4">
      {error && <p role="alert">{error}</p>}
      <MeetingAudioPlayer key={`${recordId}-${refresh}`} meeting_id={recordId} />
      {processing && <p role="status">Processing recording. This panel updates automatically.</p>}
      {status === "failed" && <p role="alert">Processing failed. Existing audio may still be playable.</p>}
      <div className="flex flex-wrap gap-2">
        <Button variant="outline" isDisabled={busy} onPress={() => act(async () => {
          const { data } = await getAudioDownloadUrl(recordId, await session.getToken());
          window.location.assign(data.url);
        })}><Download size={16} />Download</Button>
        <Button variant="outline" onPress={copyLink}><Share2 size={16} />Copy link</Button>
        <Button variant="outline" onPress={() => setRefresh(n => n + 1)}><RefreshCw size={16} />Refresh</Button>
        {canEdit && <>
          <Button variant="outline" isDisabled={busy} onPress={() => act(() => update({ favourite: !record.favourite }))}><Star size={16} fill={record.favourite ? "currentColor" : "none"} />Favourite</Button>
          <Button variant="outline" isDisabled={busy} onPress={() => {
            const title = window.prompt("Recording title", record.title);
            if (title?.trim()) act(() => update({ title: title.trim() }), "Title updated");
          }}><Pencil size={16} />Rename</Button>
          <Button variant="danger" isDisabled={busy || processing} onPress={remove}><Trash2 size={16} />Delete</Button>
          <Button variant="outline" isDisabled={busy || processing} onPress={retry}>Reprocess</Button>
        </>}
      </div>
      <Tabs selectedKey={tab} onSelectionChange={setTab}>
        <Tabs.ListContainer><Tabs.List aria-label="Recording information">
          {["summary", "transcript", "chapters", "details"].map(key => <Tabs.Tab id={key} key={key}>{key}<Tabs.Indicator /></Tabs.Tab>)}
        </Tabs.List></Tabs.ListContainer>
        <Tabs.Panel id="summary">
          {record.insights?.topics?.length > 0 && <div className="flex flex-wrap gap-2 pt-4">{record.insights.topics.map((topic, index) => <span className="rounded-full bg-accent-soft px-3 py-1 text-xs" key={index}>{topic}</span>)}</div>}
          <p className="whitespace-pre-wrap py-4">{summary.map(r => r.text).join("\n\n") || emptyText}</p></Tabs.Panel>
        <Tabs.Panel id="transcript"><p className="whitespace-pre-wrap py-4">{transcript.map(r => r.text).join("\n\n") || emptyText}</p></Tabs.Panel>
        <Tabs.Panel id="chapters"><div className="flex flex-col gap-4 py-4">
          <p className="text-sm text-muted">AI-generated sections in transcript order.</p>
          {record.insights?.chapters?.length ? record.insights.chapters.map((chapter, index) => <section key={index}>
            <h3 className="font-semibold">{index + 1}. {chapter.title}</h3><p className="text-sm whitespace-pre-wrap">{chapter.summary}</p>
          </section>) : <p>{emptyText}</p>}
        </div></Tabs.Panel>
        <Tabs.Panel id="details"><dl className="py-4 text-sm space-y-2">
          <div><dt>Recorded by</dt><dd>{record.created_by?.user_email || "Unknown"}</dd></div>
          <div><dt>Participants</dt><dd>{record.members?.map(m => m.member_fullname || m.member_id).join(", ")}</dd></div>
          <div><dt>Audio size</dt><dd>{record.recording?.bytes != null ? `${(record.recording.bytes / 1024**2).toFixed(2)} MB` : "Not measured for this recording"}</dd></div>
          <div><dt>Recorded duration</dt><dd>{record.recording?.duration_seconds != null ? `${Math.round(record.recording.duration_seconds)} seconds` : "Not measured for this recording"}</dd></div>
        </dl></Tabs.Panel>
      </Tabs>
    </Card.Content>
  </Card>;
}
